import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { API_URL } from '../config/api';
import LoadingIndicator from '../Components/LoadingIndicator';

const MAX_LINKS = 5;
const MAX_PRODUCT_IMAGES = 5;
const PRODUCT_IMAGE_PRESIGN_BATCH = 5;

const revokePreviewIfBlob = (previewUrl) => {
  if (previewUrl && String(previewUrl).startsWith('blob:')) {
    URL.revokeObjectURL(previewUrl);
  }
};

const ContentEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [selectedCategoryOption, setSelectedCategoryOption] = useState(null);
  const [categoryInputValue, setCategoryInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  /** { key, previewUrl, fileName } — previewUrl is signed GET or blob: for new uploads */
  const [productImages, setProductImages] = useState([]);
  const productImagesRef = useRef(productImages);
  productImagesRef.current = productImages;

  useEffect(() => {
    return () => {
      productImagesRef.current.forEach((item) =>
        revokePreviewIfBlob(item.previewUrl)
      );
    };
  }, []);

  const [formData, setFormData] = useState({
    categoryId: '',
    rfqNo: '',
    rfqDate: '',
    rfqDueDateTime: '',
    emdPayment: 0,
    emdVisible: false,
    tenderFee: 0,
    tenderFeeVisible: false,
    requirementSummary: '',
    summary: '',
    referenceLink: [''],
    supportContact: '',
    leadVisibility: [],
    status: 'active'
  });

  // ── Category: load once, filter client-side ────────────────────────────────

  const fetchAllCategories = async () => {
    if (!API_URL) return;
    setIsLoadingCategories(true);
    try {
      const response = await axios.post(
        API_URL,
        { action: 'getActiveCategories' },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const list = Array.isArray(response.data?.data) ? response.data.data : [];
      const mapped = list
        .filter(Boolean)
        .map((item) => ({ _id: item._id, category: item.category?.toUpperCase() }))
        .sort((a, b) => a.category.localeCompare(b.category));
      setAllCategories(mapped);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to load categories');
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const filterCategories = (search) => {
    if (!search) return allCategories;
    const term = search.toUpperCase();
    const startsWith = allCategories.filter((cat) => cat.category.startsWith(term));
    const contains = allCategories.filter(
      (cat) => !cat.category.startsWith(term) && cat.category.includes(term)
    );
    return [...startsWith, ...contains];
  };

  const categoryOptions = useMemo(() => {
    return filterCategories(categoryInputValue).map((cat) => ({
      value: cat._id,
      label: cat.category,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCategories, categoryInputValue]);

  const isFormValid = useMemo(() => {
    return (
      (formData.categoryId || '').trim() !== '' &&
      (formData.rfqNo || '').trim() !== '' &&
      (formData.rfqDate || '').trim() !== '' &&
      (formData.rfqDueDateTime || '').trim() !== '' &&
      (formData.requirementSummary || '').trim() !== '' &&
      formData.referenceLink.some((r) => r.trim() !== '') &&
      (formData.supportContact || '').trim() !== '' &&
      Array.isArray(formData.leadVisibility) &&
      formData.leadVisibility.length > 0
    );
  }, [formData]);

  useEffect(() => {
    const fetchContent = async () => {
      if (!API_URL) {
        toast.error('API URL not configured.');
        return;
      }
      if (!id) {
        toast.error('Content ID missing.');
        return;
      }

      setIsLoadingData(true);
      setProductImages((prev) => {
        prev.forEach((item) => revokePreviewIfBlob(item.previewUrl));
        return [];
      });
      try {
        const response = await axios.post(
          API_URL,
          { action: 'getContent', contentId: id },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const data = response.data;
        if (!data) {
          toast.error('Content not found.');
          navigate('/content');
          return;
        }
        // Format dates
        const rfqDate = data.rfqDate
          ? new Date(data.rfqDate).toISOString().split('T')[0]
          : '';
        const rfqDueDateTime = data.rfqDueDateTime
          ? new Date(data.rfqDueDateTime).toISOString().slice(0, 16)
          : '';

        setSelectedCategoryOption(
          data.category
            ? { value: data.category._id || data.category, label: (data.category.category || data.category)?.toUpperCase() }
            : null
        );
        setFormData({
          categoryId: data.category?._id || data.category || '',
          rfqNo: data.rfqNo || '',
          rfqDate: rfqDate,
          rfqDueDateTime: rfqDueDateTime,
          emdPayment: data.emdPayment ? Math.round(data.emdPayment * 100) : 0,
          emdVisible: Boolean(data.emdVisible),
          tenderFee: data.tenderFee ? Math.round(data.tenderFee * 100) : 0,
          tenderFeeVisible: Boolean(data.tenderFeeVisible),
          requirementSummary: data.requirementSummary || '',
          summary: data.summary || '',
          referenceLink: Array.isArray(data.referenceLink)
            ? data.referenceLink.slice(0, 5)
            : data.referenceLink
            ? [data.referenceLink]
            : [''],

          supportContact: data.supportContact || '',
          leadVisibility: Array.isArray(data.leadVisibility)
            ? data.leadVisibility
            : [],
          status: 'active'
        });

        const fromProfile = Array.isArray(data.profileImageUrl)
          ? data.profileImageUrl.filter((k) => k && String(k).trim())
          : [];
        const keys = fromProfile.length
          ? [...new Set(fromProfile)]
          : data.productImageKey
            ? [data.productImageKey]
            : [];

        if (keys.length) {
          const loaded = await Promise.all(
            keys.map(async (productImageKey) => {
              try {
                const res = await axios.post(
                  API_URL,
                  { action: 'getProductImageUrl', productImageKey },
                  { headers: { 'Content-Type': 'application/json' } }
                );
                const viewUrl = res.data?.viewUrl || '';
                if (!viewUrl) return null;
                return {
                  key: productImageKey,
                  previewUrl: viewUrl,
                  fileName:
                    productImageKey.split('/').pop() || 'Product image'
                };
              } catch {
                return null;
              }
            })
          );
          setProductImages(loaded.filter(Boolean));
        } else {
          setProductImages([]);
        }
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Failed to load content';
        toast.error(message);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchContent();
    fetchAllCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const formatIndianNumber = (cents) => {
    if (!cents || cents === 0) return '0.00';
    const number = parseFloat(cents) / 100;
    return number.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleReferenceChange = (index, value) => {
    const updated = [...formData.referenceLink];
    updated[index] = value;
    setFormData((prev) => ({ ...prev, referenceLink: updated }));
  };

  const addReferenceLink = () => {
    if (formData.referenceLink.length >= MAX_LINKS) return;
    setFormData((prev) => ({
      ...prev,
      referenceLink: [...prev.referenceLink, '']
    }));
  };

  const removeReferenceLink = (index) => {
    const updated = formData.referenceLink.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      referenceLink: updated.length ? updated : ['']
    }));
  };

  const handleNumberInput = (e, fieldName) => {
    const key = e.key;
    const input = e.target;
    const isTextSelected = input.selectionStart !== input.selectionEnd;
    
    if (key === 'Backspace' || key === 'Delete') {
      e.preventDefault();
      if (isTextSelected) {
        setFormData((prev) => ({ ...prev, [fieldName]: 0 }));
      } else {
        setFormData((prev) => {
          const currentValue = prev[fieldName] || 0;
          const newValue = Math.floor(currentValue / 10);
          return { ...prev, [fieldName]: newValue };
        });
      }
    } else if (key >= '0' && key <= '9') {
      e.preventDefault();
      if (isTextSelected) {
        setFormData((prev) => ({
          ...prev,
          [fieldName]: parseInt(key)
        }));
      } else {
        setFormData((prev) => {
          const currentValue = prev[fieldName] || 0;
          const newValue = currentValue * 10 + parseInt(key);
          return { ...prev, [fieldName]: newValue };
        });
      }
    } else if (key === 'a' && (e.ctrlKey || e.metaKey)) {
      return;
    } else if (key === 'Tab') {
      return;
    } else {
      e.preventDefault();
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    let formatted = value;

    if (value && value.includes('-')) {
      const parts = value.split('-');
      if (parts[0] && parts[0].length > 4) {
        parts[0] = parts[0].substring(0, 4);
        formatted = parts.join('-');
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formatted
    }));
  };

  const handleLeadVisibilityChange = (value) => {
    setFormData((prev) => {
      const exists = prev.leadVisibility.includes(value);
      const updated = exists
        ? prev.leadVisibility.filter((item) => item !== value)
        : [...prev.leadVisibility, value];
      return { ...prev, leadVisibility: updated };
    });
  };

  const putFileToPresignedUrl = async (uploadUrl, file, contentType) => {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType }
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(
        text || `Upload to storage failed (${res.status} ${res.statusText})`
      );
    }
  };

  const extractPresignResponse = (body, fileCount) => {
    const raw =
      body &&
      typeof body === 'object' &&
      body.data &&
      typeof body.data === 'object' &&
      !Array.isArray(body.data)
        ? { ...body, ...body.data }
        : body || {};

    let uploadUrls = Array.isArray(raw.uploadUrls) ? raw.uploadUrls : [];
    if (uploadUrls.length === 0 && raw.uploadUrl) {
      uploadUrls = [raw.uploadUrl];
    }

    let keys = Array.isArray(raw.profileImageUrl)
      ? raw.profileImageUrl
      : typeof raw.profileImageUrl === 'string' && raw.profileImageUrl
        ? [raw.profileImageUrl]
        : Array.isArray(raw.profileImageUrls)
          ? raw.profileImageUrls
          : Array.isArray(raw.productImageKeys)
            ? raw.productImageKeys
            : [];

    if (keys.length === 0 && raw.productImageKey) {
      keys = [raw.productImageKey];
    }

    if (uploadUrls.length !== fileCount || keys.length !== fileCount) {
      throw new Error(
        `Presign mismatch: expected ${fileCount} URL(s) and key(s), got uploadUrls=${uploadUrls.length}, keys=${keys.length}.`
      );
    }

    return { uploadUrls, keys };
  };

  const convertToWebP = (file, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("WebP conversion failed"));

          const webpFile = new File(
            [blob],
            file.name.replace(/\.\w+$/, ".webp"), 
            { type: "image/webp" }
          );

          resolve(webpFile);
        },
        "image/webp",
        quality
      );
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const uploadProductImageBatch = async (files) => {
  if (!files.length || files.length > PRODUCT_IMAGE_PRESIGN_BATCH) {
    throw new Error(
      `files must contain 1–${PRODUCT_IMAGE_PRESIGN_BATCH} entries`
    );
  }

  const webpFiles = await Promise.all(
    files.map((file) => convertToWebP(file))
  );

  const presignPayload = {
    action: 'getProductImageUploadUrl',
    files: webpFiles.map((f) => ({
      fileName: f.name,          
      contentType: "image/webp"  
    }))
  };

  const { data } = await axios.post(API_URL, presignPayload, {
    headers: { 'Content-Type': 'application/json' }
  });

  const { uploadUrls, keys } = extractPresignResponse(data, webpFiles.length);

  await Promise.all(
    webpFiles.map((file, i) =>
      putFileToPresignedUrl(uploadUrls[i], file, "image/webp")
    )
  );

  return webpFiles.map((file, i) => ({
    key: keys[i],
    previewUrl: URL.createObjectURL(file),
    fileName: file.name
  }));
};

  const handleFileChange = async (e) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';

    if (!selectedFiles.length) return;

    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }

    const isLikelyImage = (f) =>
      f.type.startsWith('image/') ||
      !f.type ||
      /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif)$/i.test(f.name);

    const picked = selectedFiles.filter(isLikelyImage);
    if (!picked.length) {
      toast.error('Please select image files only.');
      return;
    }

    const remaining = MAX_PRODUCT_IMAGES - productImages.length;
    if (remaining <= 0) {
      toast.error(`You can upload at most ${MAX_PRODUCT_IMAGES} images.`);
      return;
    }

    const toUpload = picked.slice(0, remaining);
    if (picked.length > remaining) {
      toast.info(
        `Only the first ${remaining} file(s) were added (max ${MAX_PRODUCT_IMAGES}).`
      );
    }

    setIsUploadingImage(true);
    try {
      const newItems = [];
      for (let i = 0; i < toUpload.length; i += PRODUCT_IMAGE_PRESIGN_BATCH) {
        const chunk = toUpload.slice(i, i + PRODUCT_IMAGE_PRESIGN_BATCH);
        const batchItems = await uploadProductImageBatch(chunk);
        newItems.push(...batchItems);
      }
      setProductImages((prev) => [...prev, ...newItems]);
      toast.success(
        newItems.length === 1
          ? 'Image uploaded'
          : `${newItems.length} images uploaded`
      );
    } catch (error) {
      console.error('[ContentEdit] upload error', error.response?.data || error.message);
      const message =
        error.response?.data?.message ||
        error.message ||
        'Image upload failed';
      toast.error(message);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const removeProductImage = (index) => {
    setProductImages((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      revokePreviewIfBlob(removed?.previewUrl);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const userId = localStorage.getItem('userid');
    if (!userId) {
      toast.error('User not found. Please login again.');
      return;
    }
    if (!formData.categoryId) {
      toast.error('Category is required.');
      return;
    }
    if (!(formData.rfqNo || '').trim()) {
      toast.error('RFQ No is required.');
      return;
    }
    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }

    const payload = {
      action: 'editContent',
      contentId: id,
      category: formData.categoryId,
      rfqNo: (formData.rfqNo || '').trim(),
      rfqDate: formData.rfqDate || '',
      rfqDueDateTime: formData.rfqDueDateTime || '',
      emdPayment: formData.emdPayment ? parseFloat(formData.emdPayment / 100) : 0,
      emdVisible: Boolean(formData.emdVisible),
      tenderFee: formData.tenderFee ? parseFloat(formData.tenderFee / 100) : 0,
      tenderFeeVisible: Boolean(formData.tenderFeeVisible),
      requirementSummary: (formData.requirementSummary || '').trim(),
      summary: (formData.summary || '').trim(),
      profileImageUrl: productImages.map((img) => img.key),
      productImageKey: productImages[0]?.key || '',
      referenceLink: formData.referenceLink
        .map((r) => r.trim())
        .filter((r) => r !== ''),
      supportContact: (formData.supportContact || '').trim(),
      leadVisibility: formData.leadVisibility || [],
      status: formData.status || 'active',
      userId: userId
    };

    setIsSubmitting(true);
    try {
      const response = await axios.post(API_URL, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success(response.data?.message || 'Content updated successfully');
      navigate(`/content`);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to update content';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading = isLoadingData || isLoadingCategories;

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Edit Lead Post</h1>
            <p>Update lead post details.</p>
          </div>
          <div className="col-sm-6 text-right subscription-header-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
        </div>
      </div>

      <div className="card employee-content-card">
        <div className="card-body">
          {isLoading ? (
            <div className="employee-loading">
              <LoadingIndicator size={120} />
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label" htmlFor="category">
                      Category <span className="required-asterisk">*</span>
                    </label>
                    <Select
                      inputId="category"
                      options={categoryOptions}
                      isLoading={isLoadingCategories}
                      inputValue={categoryInputValue}
                      onInputChange={(val) => setCategoryInputValue(val)}
                      value={selectedCategoryOption}
                      onChange={(selected) => {
                        setSelectedCategoryOption(selected);
                        setCategoryInputValue('');
                        setFormData((prev) => ({ ...prev, categoryId: selected ? selected.value : '' }));
                      }}
                      placeholder="Select or search category..."
                      isClearable
                      filterOption={() => true}
                      classNamePrefix="react-select"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label" htmlFor="rfqNo">
                      RFQ No <span className="required-asterisk">*</span>
                    </label>
                    <input
                      id="rfqNo"
                      name="rfqNo"
                      type="text"
                      className="form-control"
                      value={formData.rfqNo}
                      onChange={handleChange}
                      placeholder="Enter RFQ No"
                      required
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label" htmlFor="rfqDate">
                      RFQ Date <span className="required-asterisk">*</span>
                    </label>
                    <input
                      id="rfqDate"
                      name="rfqDate"
                      type="date"
                      className="form-control"
                      value={formData.rfqDate}
                      onChange={handleDateChange}
                      required
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label" htmlFor="rfqDueDateTime">
                      RFQ Due Date & Time <span className="required-asterisk">*</span>
                    </label>
                    <input
                      id="rfqDueDateTime"
                      name="rfqDueDateTime"
                      type="datetime-local"
                      className="form-control"
                      value={formData.rfqDueDateTime}
                      onChange={handleDateChange}
                      required
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label" htmlFor="emdPayment">
                      EMD Value
                    </label>
                    <input
                      id="emdPayment"
                      name="emdPayment"
                      type="text"
                      className="form-control number-input"
                      value={formatIndianNumber(formData.emdPayment)}
                      onKeyDown={(e) => handleNumberInput(e, 'emdPayment')}
                      readOnly
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label">EMD Visible</label>
                    <label className="switch-toggle">
                      <input
                        id="emdVisible"
                        name="emdVisible"
                        type="checkbox"
                        checked={formData.emdVisible}
                        onChange={handleChange}
                      />
                      <span className="switch-slider" data-on="On" data-off="Off" />
                    </label>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label" htmlFor="tenderFee">
                      Tender Fee
                    </label>
                    <input
                      id="tenderFee"
                      name="tenderFee"
                      type="text"
                      className="form-control number-input"
                      value={formatIndianNumber(formData.tenderFee)}
                      onKeyDown={(e) => handleNumberInput(e, 'tenderFee')}
                      readOnly
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label">Tender Fee Visible</label>
                    <label className="switch-toggle">
                      <input
                        id="tenderFeeVisible"
                        name="tenderFeeVisible"
                        type="checkbox"
                        checked={formData.tenderFeeVisible}
                        onChange={handleChange}
                      />
                      <span className="switch-slider" data-on="On" data-off="Off" />
                    </label>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label" htmlFor="requirementSummary">
                      Requirement Summary <span className="required-asterisk">*</span>
                    </label>
                    <textarea
                      id="requirementSummary"
                      name="requirementSummary"
                      className="form-control"
                      rows="6"
                      value={formData.requirementSummary}
                      onChange={handleChange}
                      placeholder="Enter requirement summary"
                      required
                    ></textarea>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label" htmlFor="summary">
                      Summary
                    </label>
                    <textarea
                      id="summary"
                      name="summary"
                      className="form-control"
                      rows="6"
                      value={formData.summary}
                      onChange={handleChange}
                      placeholder="Enter summary"
                    ></textarea>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label" htmlFor="productImage">
                      Product image
                      {productImages.length > 0
                        ? ` (${productImages.length}/${MAX_PRODUCT_IMAGES})`
                        : ''}
                    </label>
                    <input
                      id="productImage"
                      name="productImage"
                      type="file"
                      className="form-control"
                      accept="image/*"
                      multiple
                      onChange={handleFileChange}
                      disabled={
                        isUploadingImage ||
                        productImages.length >= MAX_PRODUCT_IMAGES
                      }
                    />
                    <small className="text-muted d-block mt-1">
                      Up to {MAX_PRODUCT_IMAGES} images. Select several at once or
                      add in batches. Remove thumbnails to drop an image before
                      Update.
                    </small>
                    {isUploadingImage && (
                      <div style={{ marginTop: '10px' }}>
                        <LoadingIndicator size={30} className="inline-loader" />
                        <span style={{ marginLeft: '10px', fontSize: '13px', color: '#6b7280' }}>
                          Uploading...
                        </span>
                      </div>
                    )}
                    {productImages.length > 0 && (
                      <div
                        className="d-flex flex-wrap"
                        style={{ marginTop: '12px', gap: 10 }}
                      >
                        {productImages.map((item, index) => (
                          <div
                            key={`${item.key}-${index}`}
                            className="position-relative"
                            style={{ width: 120 }}
                          >
                            <img
                              src={item.previewUrl}
                              alt={item.fileName || 'Product'}
                              style={{
                                width: 120,
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 8,
                                border: '1px solid #e5e7eb'
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              style={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                padding: '2px 6px',
                                lineHeight: 1
                              }}
                              onClick={() => removeProductImage(index)}
                              title="Remove"
                              disabled={isUploadingImage}
                            >
                              <i className="fa-solid fa-times" aria-hidden />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
<div className="col-md-6">
                  <div className="form-group">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="profile-label mb-0">
                        Reference Links{" "}
                        <span className="required-asterisk">*</span>
                      </label>

                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addReferenceLink}
                        disabled={formData.referenceLink.length >= MAX_LINKS}
                      >
                        + Add
                      </button>
                    </div>

                    {formData.referenceLink.map((ref, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center mb-2"
                      >
                        <input
                          type="url"
                          className="form-control"
                          placeholder="https://"
                          value={ref}
                          onChange={(e) =>
                            handleReferenceChange(index, e.target.value)
                          }
                        />

                        {formData.referenceLink.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-link text-danger ml-2 p-0"
                            onClick={() => removeReferenceLink(index)}
                            title="Delete"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        )}
                      </div>
                    ))}
                    {formData.referenceLink.length >= MAX_LINKS && (
                      <small className="text-danger d-block mt-1">
                        Maximum 5 reference links allowed
                      </small>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label" htmlFor="supportContact">
                      Support Contact <span className="required-asterisk">*</span>
                    </label>
                    <input
                      id="supportContact"
                      name="supportContact"
                      type="text"
                      className="form-control"
                      value={formData.supportContact}
                      onChange={handleChange}
                      placeholder="+91-9999999999"
                      required
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label">
                      Lead Visibility <span className="required-asterisk">*</span>
                    </label>
                    <div className="row">
                      <div className="col-6">
                        <div className="d-flex align-items-center">
                          <span className="visibility-label">Paid</span>
                          <label className="switch-toggle ml-2">
                            <input
                              type="checkbox"
                              checked={formData.leadVisibility.includes('Paid')}
                              onChange={() => handleLeadVisibilityChange('Paid')}
                            />
                            <span className="switch-slider" data-on="On" data-off="Off" />
                          </label>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="d-flex align-items-center">
                          <span className="visibility-label">Free</span>
                          <label className="switch-toggle ml-2">
                            <input
                              type="checkbox"
                              checked={formData.leadVisibility.includes('Free')}
                              onChange={() => handleLeadVisibilityChange('Free')}
                            />
                            <span className="switch-slider" data-on="On" data-off="Off" />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* <div className="col-md-6">
                  <div className="form-group">
                    <label className="profile-label" htmlFor="status">
                      Status
                    </label>
                    <select
                      id="status"
                      name="status"
                      className="form-control"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div> */}
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!isFormValid || isSubmitting || isUploadingImage}
                >
                  {isSubmitting ? (
                    <LoadingIndicator size={22} className="inline-loader" />
                  ) : (
                    'Update'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentEdit;
